/*
 * Authors: mariushirjanu
 * Created: 09/04/2024
 */
import * as _ from 'underscore';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BaseReportActionsDialog } from '../base-report-actions-dialog';
import { GaValidateService } from '../../../formly/ga-validate.service';
import { GaEventService } from '../../../services/ga-event.service';
import { IContact, IObject } from '../../../../../type_definitions/datatypes';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../formly/ga-formly.service';
import { Override } from '../../../app-decorators';
import { FormattingService } from '../../../services/formatting.service';
import { CancelIdContact, ContactDataService } from '../../../contact/contact-data.service';
import { AuthService } from '../../../services/auth/auth.service';
import { TranslationService } from '../../../translation/translation.service';

export enum EmailOption {
  NAM_CM = 'NAM_CM',
  ME = 'ME',
  CONTACTS = 'CONTACTS',
  NONE = 'NONE'
}

interface MailNewWebshopActionModel {
  optionTo?:EmailOption;
  optionCC?:EmailOption;
  contactsTo?:IContact[];
  contactsCC?:IContact[];
  //
  mailToNAMAndCM?:boolean;
  mailTo:string;
  mailCc:string;
  subject:string;
}

@Component({
  selector: 'mail-new-webshop-action',
  templateUrl: '../../../message/dialogs/default-form-dialog.html',
  styleUrls: ['../report-actions.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MailNewWebshopActionComponent extends BaseReportActionsDialog implements OnInit {
  private contactsToField:IExtraFieldConfigurationObject;
  private contactsCCField:IExtraFieldConfigurationObject;
  public model:MailNewWebshopActionModel;
  private contactLabel:string = 'Contact';

  constructor(
    private authService:AuthService,
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private contactDataService:ContactDataService,
    protected translationService:TranslationService,
    eventService:GaEventService,
    validateService:GaValidateService
  ) {
    super(eventService, validateService);
  }

  public ngOnInit(): void {
    this.saveButtonLabel = 'Send';

    super.ngOnInit();

    // init model
    this.model = <any>{
      optionTo: EmailOption.NAM_CM,
      optionCC: EmailOption.NONE,
      mailToNAMAndCM: true,
      subject: 'New webshop notification'
    };

    // create fields
    this.fields = this.createFields();
  }

  @Override(BaseReportActionsDialog)
  public getModel(): IObject {
    const userId:number = this.authService.getCurrentUser().id;
    const mailTo:string = this.model?.optionTo === EmailOption.ME ? `${userId}` : this.formattingService.formatRecipients(this.model?.contactsTo);
    const mailCC:string = this.model?.optionCC === EmailOption.ME ? `${userId}` : this.formattingService.formatRecipients(this.model?.contactsCC);

    return {
      mailToNAMAndCM: this.model?.mailToNAMAndCM,
      mailTo,
      mailCC: this.model?.optionCC !== EmailOption.NONE ? mailCC : undefined,
      subject: this.model?.subject
    };
  }

  private createFields(): IExtraFieldConfigurationObject[] {
    const labelClass: string = 'col-md-2';
    const valueClass: string = 'col-md-10';

    return [
      this.formlyService.aGroup('toGroup', 'To')
        .withWrapperClass('col-md-12')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFields([
          this.formlyService.aRadioButton('optionTo', 'NAMs & Country Manager')
            .withOption(EmailOption.NAM_CM)
            .withoutWrapper()
            .onChange((selectedOption:EmailOption) => this.onOptionToFieldChange(selectedOption, EmailOption.NAM_CM))
            .build(),

          this.formlyService.aRadioButton('optionTo', 'Myself')
            .withOption(EmailOption.ME)
            .withoutWrapper()
            .onChange((selectedOption:EmailOption) => this.onOptionToFieldChange(selectedOption, EmailOption.ME))
            .build(),

          this.formlyService.aGroup()
            .withLabelClass('d-none')
            .withValueClass('col-md-12')
            .withFields([
              this.formlyService.aRadioButton('optionTo')
                .withOption(EmailOption.CONTACTS)
                .withoutWrapper()
                .withValueClass('col-md-1')
                .onChange((selectedOption:EmailOption) => this.onOptionToFieldChange(selectedOption, EmailOption.CONTACTS))
                .build(),

              this.contactsToField = this.formlyService.aMultiSelect('contactsTo', this.contactLabel)
                .isRequired()
                .isDisabled()
                .withoutWrapper()
                .withValueClass('col-md-11 email-addresses')
                .withFormat(this.formattingService.formatContactWithCompany)
                .withExtraOptions({
                  templateOptions: {
                    searchFunction: {
                      request: (filter:string) => this.contactDataService.getContacts(filter, CancelIdContact.TO),
                      cancel: () => this.contactDataService.cancelGetContacts(CancelIdContact.TO)
                    }
                  }
                })
                .build()
            ])
            .build()
        ])
        .build(),


      this.formlyService.aGroup('ccGroup', 'CC')
        .withWrapperClass('col-md-12')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFields([
          this.formlyService.aRadioButton('optionCC', 'Myself')
            .withOption(EmailOption.ME)
            .withoutWrapper()
            .onChange((selectedOption:EmailOption) => this.onOptionCCFieldChange(selectedOption, EmailOption.ME))
            .build(),

          this.formlyService.aGroup()
            .withLabelClass('d-none')
            .withValueClass('col-md-12')
            .withFields([
              this.formlyService.aRadioButton('optionCC')
                .withOption(EmailOption.CONTACTS)
                .withoutWrapper()
                .withValueClass('col-md-1')
                .onChange((selectedOption:EmailOption) => this.onOptionCCFieldChange(selectedOption, EmailOption.CONTACTS))
                .build(),

              this.contactsCCField = this.formlyService.aMultiSelect('contactsCC', this.contactLabel)
                .isRequired()
                .isDisabled()
                .withoutWrapper()
                .withValueClass('col-md-11 email-addresses')
                .withFormat(this.formattingService.formatContactWithCompany)
                .withExtraOptions({
                  templateOptions: {
                    searchFunction: {
                      request: (filter:string) => this.contactDataService.getContacts(filter, CancelIdContact.CC),
                      cancel: () => this.contactDataService.cancelGetContacts(CancelIdContact.CC)
                    }
                  }
                })
                .build()
            ])
            .build(),

          this.formlyService.aRadioButton('optionCC', 'None')
            .withOption(EmailOption.NONE)
            .withoutWrapper()
            .withWrapperClass('option-none')
            .onChange((selectedOption:EmailOption) => this.onOptionCCFieldChange(selectedOption, EmailOption.NONE))
            .build()
        ])
        .build(),

      this.formlyService.anInput('subject', 'Subject')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build()
    ];
  }

  private onOptionToFieldChange = (selectedOption:EmailOption, buttonOption:EmailOption):void => {
    if(selectedOption === buttonOption) { // trigger only once
      this.contactsToField.templateOptions.disabled = _.isEmpty(selectedOption) || selectedOption !== EmailOption.CONTACTS;
      this.model.mailToNAMAndCM = selectedOption === EmailOption.NAM_CM || undefined;
    }
  };

  private onOptionCCFieldChange = (selectedOption:EmailOption, buttonOption:EmailOption):void => {
    if(selectedOption === buttonOption) { // trigger only once
      this.contactsCCField.templateOptions.disabled = _.isEmpty(selectedOption) || selectedOption !== EmailOption.CONTACTS;
    }
  };

  @Override(BaseReportActionsDialog)
  public validate(): Promise<string[] | void> {
    const extraErrors:string[] = [];
    if((this.model?.optionTo === EmailOption.CONTACTS && !this.model?.contactsTo) || (this.model?.optionCC === EmailOption.CONTACTS && !this.model?.contactsCC)){
      this.contactsToField.formControl?.markAsDirty();
      this.contactsCCField.formControl?.markAsDirty();
      extraErrors.push(this.translationService.instant('GA.VALIDATION.REQUIRED', { label: this.contactLabel }));
    }
    return super.validate(extraErrors);
  }
}
